import { minMax } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import { SparklesSmall } from '@wix/wix-ui-icons-common';
import { TextSelection } from 'prosemirror-state';
import React from 'react';

import type { IEditorQuery } from './../ricos-content-query';
import type { ModalService } from './../ricos-modals';
import type { EditorPluginCreator, QuickAction, TiptapEditor } from './../ricos-types';
import { quickActionsModalSections, ToolbarType } from './../ricos-types';
import { RESOLVERS_IDS } from './../toolbars-v3/lib/resolvers-ids';
import type { TiptapAdapterServices } from './../tiptap-editor/initializeTiptapAdapter';

import { AiActionsModal } from './ai-actions-modal/ai-actions-modal';
import { AiContentModal } from './ai-content-modal/ai-content-modal';
import { AiFlowSelectionModal } from './ai-flow-selection-modal/ai-flow-selection-modal';
import { AiImageModal } from './ai-image-modal/ai-image-modal';
import { TEXT_BUTTON_AI } from './data-hooks';
import type { DiscardPayload } from './discard-confirmation-modal/discard-confirmation-modal';
import { DiscardConfirmationModal } from './discard-confirmation-modal/discard-confirmation-modal';
import { createAltTextInput } from './exports/image-alt-text-input/alt-text-input';
import { createCaptionTrigger } from './exports/image-caption-integration/caption-trigger';
import { FloatingToolbarButton } from './floating-toolbar-button/floating-toolbar-button';
import { tiptapExtensions } from './tiptap/extensions';
import { ToolbarButton } from './toolbar/toolbar-button';
import { ToolbarButtonSmall } from './toolbar/toolbar-button-small';
import type { AiPluginEditorConfig, OpenDiscardModalFn, Prompt, ReferenceElement } from './types';
import {
  AI_ACTIONS_MODAL_ID,
  AI_DISCARD_MODAL_ID,
  AI_FLOW_SELECTION_MODAL_ID,
  AI_IMAGE_MODAL_ID,
  AI_MODAL_ID,
  AI_TYPE,
} from './types';
import { getPromptBiValue } from './utils/get-prompt-bi';

type EntryType = ToolbarType.FORMATTING | ToolbarType.SIDE | 'FLOW_SELECTION' | 'EXTERNAL';

const DEFAULT_CONFIG: AiPluginEditorConfig = {
  appDefId: '',
  showToolbarButtonText: true,
  enableImageGeneration: true,
  enableImageCaptionExport: true,
  enableImageAltTextExport: true,
};

export const pluginAi: EditorPluginCreator<
  AiPluginEditorConfig,
  { AiContentModal: typeof AiContentModal; AiImageModal: typeof AiImageModal }
> = pluginConfig => {
  const config = { ...DEFAULT_CONFIG, ...pluginConfig };
  const openDiscardModal: OpenDiscardModalFn = (
    modalsService: ModalService,
    referenceElement: ReferenceElement
  ) => {
    return new Promise<DiscardPayload>(resolve => {
      modalsService.openModal({
        id: AI_DISCARD_MODAL_ID,
        Component: DiscardConfirmationModal,
        componentProps: {
          closeModal: (confirmed: DiscardPayload) => {
            modalsService.closeModal(AI_DISCARD_MODAL_ID);
            resolve(confirmed);
          },
        },
        onClickOutside: () => {
          modalsService.closeModal(AI_DISCARD_MODAL_ID);
          resolve({ confirmed: false });
        },
        layout: 'baseDialog',
        returnFocusOnDeactivate: true,
        positioning: {
          referenceElement,
        },
        disableArrowNavigation: true,
      });
    });
  };

  const toggleAiModal =
    (entryType: EntryType) =>
    (editor: TiptapEditor, modalsService: ModalService, initialPrompt?: Prompt) =>
      modalsService.togglePluginPanel({
        id: AI_MODAL_ID,
        Component: AiContentModal,
        componentProps: {
          closeModal: () => modalsService.closeModal(AI_MODAL_ID),
          openDiscardModal: (referenceElement: ReferenceElement) =>
            openDiscardModal(modalsService, referenceElement),
          config,
          initialPromptOptions: initialPrompt
            ? { prompt: initialPrompt, autoTrigger: true }
            : undefined,
        },
        isMobile: false,
        onClickOutside: () => {},
        positioning: {
          referenceElement: getFakeReferenceElement(editor),
          placement: 'right-start',
        },
        dropdownValue: initialPrompt ? getPromptBiValue(initialPrompt) : undefined,
        entryType,
      });

  const toggleToolbarModal =
    (entryType: EntryType, modalType?: 'exclusive') =>
    (_editor: TiptapEditor, modalsService: ModalService, referenceElement: HTMLDivElement) => {
      modalsService.isModalOpen(AI_ACTIONS_MODAL_ID)
        ? modalsService.closeModal(AI_ACTIONS_MODAL_ID)
        : modalsService.openModal({
            id: AI_ACTIONS_MODAL_ID,
            Component: AiActionsModal,
            componentProps: {
              toggleAiContentModal: toggleAiModal(entryType),
              toggleAiImageModal: toggleAiImageModal(entryType),
              enableImageGeneration: config.enableImageGeneration,
              config,
            },
            layout: 'base',
            positioning: {
              referenceElement,
              placement: 'bottom-start',
            },
            type: modalType,
            entryType,
            disableArrowNavigation: true,
          });
    };

  const toggleAiFlowSelectionModal =
    (entryType: EntryType) =>
    (_editor: TiptapEditor, modalsService: ModalService, referenceElement: HTMLDivElement) => {
      modalsService.isModalOpen(AI_FLOW_SELECTION_MODAL_ID)
        ? modalsService.closeModal(AI_FLOW_SELECTION_MODAL_ID)
        : modalsService.openModal({
            id: AI_FLOW_SELECTION_MODAL_ID,
            Component: AiFlowSelectionModal,
            componentProps: {
              toggleAiContentModal: toggleAiModal('FLOW_SELECTION'),
              toggleAiImageModal: toggleAiImageModal('FLOW_SELECTION'),
            },
            layout: 'base',
            positioning: {
              referenceElement,
              placement: 'bottom-start',
            },
            type: 'exclusive',
            entryType,
            disableArrowNavigation: true,
          });
    };

  const toggleAiImageModal =
    (entryType: EntryType) =>
    (editor: TiptapEditor, modalsService: ModalService, convertSelectionToPrompt?: boolean) =>
      modalsService.togglePluginPanel({
        id: AI_IMAGE_MODAL_ID,
        Component: AiImageModal,
        componentProps: {
          closeModal: () => modalsService.closeModal(AI_IMAGE_MODAL_ID),
          openDiscardModal: (referenceElement: ReferenceElement) =>
            openDiscardModal(modalsService, referenceElement),
          convertSelectionToPrompt,
          config,
          insertActions: entryType === 'FLOW_SELECTION' ? 'single' : 'both',
        },
        onClickOutside: () => {},
        positioning: {
          referenceElement: getFakeReferenceElement(editor),
          placement: 'right-end',
        },
        entryType,
        isMobile: false,
      });

  // Checks whether there is something to work on for ai, or if there is no text under the cursor.
  const checkContextualMode = ({
    editorQuery,
    nodeId,
  }: {
    editorQuery: IEditorQuery['query'];
    nodeId?: string;
  }) => {
    if (editorQuery.isEmptyTextBlock()) {
      return false;
    }

    if (editorQuery.isOnlySlashInParagraph()) {
      return false;
    }

    if (nodeId) {
      if (editorQuery.isChildSingleTextBlockEmpty(nodeId)) {
        return false;
      }

      return editorQuery.isChildSingleTextBlock(nodeId);
    }

    return (
      editorQuery.isMultipleSelection() ||
      editorQuery.isSingleRootTextBlock() ||
      editorQuery.isListItem()
    );
  };

  return {
    config,
    type: AI_TYPE,
    tiptapExtensions,
    textButtons: [
      {
        id: AI_TYPE,
        type: 'modal',
        presentation: {
          dataHook: TEXT_BUTTON_AI,
          tooltip: 'AIPlugin_ToolbarButton_Tooltip',
          component: (props: $TSFixMe) => (
            <ToolbarButton {...props} showLabel={config.showToolbarButtonText} />
          ),
          icon: (props: $TSFixMe) => (
            <ToolbarButtonSmall {...props} showLabel={config.showToolbarButtonText} />
          ), // Rendering in external toolbar
        },
        attributes: {
          visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
          disabled: RESOLVERS_IDS.IS_NODE_OR_CAPTION_SELECTED,
        },
        command:
          ({ editor, modalService, editorQuery }) =>
          event => {
            if (
              checkContextualMode({ editorQuery: editorQuery.query }) &&
              event.target &&
              // No activeElement in JSDOM tests, always assume editor is focused
              (event.view?.document ? event.view?.document.activeElement === editor.view.dom : true)
            ) {
              const { from, to } = editor.state.selection;

              if (from === to) {
                editor.commands.selectParentNode();
              }

              toggleToolbarModal(ToolbarType.FORMATTING)(editor, modalService, event.target);
            } else {
              if (config.enableImageGeneration) {
                toggleAiFlowSelectionModal(ToolbarType.FORMATTING)(
                  editor,
                  modalService,
                  event.target
                );
                return false;
              }

              toggleAiModal(ToolbarType.FORMATTING)(editor, modalService);
            }
            return false;
          },
      },
    ],
    floatingToolbarButton: {
      Component: ({ position, className }) => (
        <FloatingToolbarButton
          toggleToolbarModal={toggleToolbarModal(ToolbarType.SIDE, 'exclusive')}
          toggleAiModal={toggleAiModal(ToolbarType.SIDE)}
          position={position}
          className={className}
          checkContextualMode={checkContextualMode}
        />
      ),
      priority: 1,
      isVisible: checkContextualMode,
    },
    getQuickActions: () => [
      {
        id: 'writeWithAi',
        title: 'QuickActionsPlugin_ActionTitle_WriteWithAI',
        icon: SparklesSmall,
        section: quickActionsModalSections.AI,
        tags: ['generate text', 'content', 'write', 'writer', 'automated'],
        command: ({ modalService, editor }) => {
          const { $anchor } = editor.view.state.selection;
          const range = $anchor.blockRange();
          if (range) {
            const selectedText = TextSelection.create(
              editor.view.state.doc,
              range?.start + 1,
              range?.end - 1
            );
            editor.view.dispatch(editor.view.state.tr.setSelection(selectedText));
          }
          modalService.togglePluginPanel({
            Component: AiContentModal,
            id: AI_MODAL_ID,
            componentProps: {
              closeModal: () => modalService.closeModal(AI_MODAL_ID),
              openDiscardModal: (referenceElement: ReferenceElement) =>
                openDiscardModal(modalService, referenceElement),
              config,
              initialPromptOptions: undefined,
            },
            positioning: {
              referenceElement: getFakeReferenceElement(editor),
              placement: 'right-start',
            },
            onClickOutside: () => {},
            isMobile: false,
          });
        },
      },
      ...(config.enableImageGeneration
        ? [
            {
              id: 'createImageWithAi',
              title: 'QuickActionsPlugin_ActionTitle_CreateImageWithAI',
              icon: SparklesSmall,
              section: quickActionsModalSections.AI,
              tags: [
                'generate image',
                'AI art',
                'AI generated image',
                'create art',
                'AI design',
                'digital art',
                'image generator',
              ],
              command: ({ modalService, editor }) => {
                const { $anchor } = editor.view.state.selection;
                const range = $anchor.blockRange();
                if (range) {
                  const selectedText = TextSelection.create(
                    editor.view.state.doc,
                    range?.start + 1,
                    range?.end - 1
                  );
                  editor.view.dispatch(editor.view.state.tr.setSelection(selectedText));
                }
                modalService.togglePluginPanel({
                  Component: AiImageModal,
                  id: AI_IMAGE_MODAL_ID,
                  componentProps: {
                    closeModal: () => modalService.closeModal(AI_IMAGE_MODAL_ID),
                    openDiscardModal: (referenceElement: ReferenceElement) =>
                      openDiscardModal(modalService, referenceElement),
                    config,
                    insertActions: 'both',
                    convertSelectionToPrompt: true,
                  },
                  positioning: {
                    referenceElement: getFakeReferenceElement(editor),
                    placement: 'right-start',
                  },
                  onClickOutside: () => {},
                  isMobile: false,
                });
              },
            } satisfies QuickAction,
          ]
        : []),
    ],
    exports: {
      aiIntegrations: {
        image: {
          captionTrigger: config.enableImageCaptionExport
            ? createCaptionTrigger(config.appDefId, openDiscardModal)
            : undefined,
          altTextInput: config.enableImageAltTextExport ? createAltTextInput(config) : undefined,
        },
      },
      ...(config.enableImageGeneration
        ? {
            modals: {
              AI_IMAGE: (editor: TiptapEditor, services: TiptapAdapterServices) =>
                toggleAiImageModal('EXTERNAL')(editor, services.modals),
            },
          }
        : {}),
    },
    __exports: {
      AiContentModal,
      AiImageModal,
    },
  };
};

const getFakeReferenceElement = (
  editor: Editor
): { getBoundingClientRect: () => DOMRect; focus: () => void } => {
  const { view } = editor;
  const { from, to } = view.state.selection;

  return {
    getBoundingClientRect: () => posToDOMRect(view, from, to),
    focus: () => view.focus(),
  };
};

// This is similar to https://github.com/ueberdosis/tiptap/blob/4474d056daf9280ebb10b31f98bb000e953132e5/packages/core/src/helpers/posToDOMRect.ts#L5
// However we adjust actual horizontal properties with editor dimensions
const posToDOMRect = (view: EditorView, from: number, to: number): DOMRect => {
  const editorRect = view.dom.getBoundingClientRect();
  const minPos = 0;
  const maxPos = view.state.doc.content.size;
  const resolvedFrom = minMax(from, minPos, maxPos);
  const resolvedEnd = minMax(to, minPos, maxPos);
  const start = view.coordsAtPos(resolvedFrom);
  const end = view.coordsAtPos(resolvedEnd, -1);
  const top = Math.min(start.top, end.top);
  const bottom = Math.max(start.bottom, end.bottom);
  const left = editorRect.left;
  const right = editorRect.right;
  const width = right - left;
  const height = bottom - top;
  const x = left;
  const y = top;
  const data = {
    top,
    bottom,
    left,
    right,
    width,
    height,
    x,
    y,
  };

  return {
    ...data,
    toJSON: () => data,
  };
};
